import {isNullOrUndefined} from 'utils/general';

export const SUPPORTED_APPLICATION_FIELD_TYPES = [
  'text',
  'longtext',
  'numeric',
  'multicheckbox',
  'singlecheckbox',
  'dropdown',
  'radio',
  'linear_scale',
  'date',
  'text_display',
  'audio_input',
  'geolocation',
] as const;

export type ApplicationFieldType =
  (typeof SUPPORTED_APPLICATION_FIELD_TYPES)[number];

interface BaseFormField {
  id: string;
  type: ApplicationFieldType;
  question: string;
}

export interface TextInputField extends BaseFormField {
  type: 'text';
  placeholder: string;
  errorMessage: string;
}

export interface LongTextInputField extends BaseFormField {
  type: 'longtext';
  description: string;
  placeholder: string;
  errorMessage: string;
}

export interface NumericInputField extends BaseFormField {
  type: 'numeric';
  placeholder: string;
  errorMessage: string;
}

export interface MultiCheckboxField extends BaseFormField {
  type: 'multicheckbox';
  description: string;
  options: string[];
  /**
   * When true, the applicant must select EVERY option or the field is invalid.
   * When false or omitted, any selection (including none) is accepted — the
   * historical behavior.
   */
  requireAll?: boolean;
  /**
   * Message shown to the applicant when the field fails validation (currently
   * only when {@link MultiCheckboxField.requireAll} is set and not all options
   * are checked). Falls back to a generic message when omitted.
   */
  errorMessage?: string;
  /**
   * When true, the applicant is offered an additional "Other" checkbox with a
   * free-text box; the typed value is stored as an extra entry in the answer
   * array. When false or omitted, only the predefined options are offered — the
   * historical behavior. The "Other" write-in is never counted towards
   * {@link MultiCheckboxField.requireAll}.
   */
  hasOther?: boolean;
}

export interface SingleCheckboxField extends BaseFormField {
  type: 'singlecheckbox';
  description: string;
  checkboxLabel: string;
  errorMessage: string;
}

export interface DropdownField extends BaseFormField {
  type: 'dropdown';
  options: string[];
  errorMessage: string;
}

/** A single-select question rendered as a list of radio buttons. */
export interface RadioField extends BaseFormField {
  type: 'radio';
  options: string[];
  errorMessage: string;
}

/**
 * A numeric rating question rendered as a row of radio buttons from `min` to
 * `max`, with optional labels anchoring each end of the scale.
 */
export interface LinearScaleField extends BaseFormField {
  type: 'linear_scale';
  /** Lowest value on the scale (typically 0 or 1). */
  min: number;
  /** Highest value on the scale (typically up to 10). */
  max: number;
  /** Optional label shown at the low end of the scale. */
  minLabel?: string;
  /** Optional label shown at the high end of the scale. */
  maxLabel?: string;
  /**
   * Message shown when the applicant does not pick a value. Falls back to a
   * generic message when omitted.
   */
  errorMessage?: string;
}

export interface DateField extends BaseFormField {
  type: 'date';
}

export interface TextField extends BaseFormField {
  type: 'text_display';
  question: string;
  subtext: string;
}

export interface AudioField extends BaseFormField {
  type: 'audio_input';
  description: string;
}

export interface GeolocationField extends BaseFormField {
  type: 'geolocation';
}

export type FormField =
  | TextInputField
  | LongTextInputField
  | NumericInputField
  | MultiCheckboxField
  | SingleCheckboxField
  | DropdownField
  | RadioField
  | LinearScaleField
  | DateField
  | TextField
  | AudioField
  | GeolocationField;

export interface FormInfo {
  /** Form title shown at the top of the application. */
  title: string;
  /** Form kind discriminator (e.g. 'form'). */
  type: string;
  /** Editable, optional fields the course admin adds. */
  fields: FormField[];
  /** Locked, required system fields (name, gender, dob, location). */
  requiredFields: FormField[];
  /**
   * Explicit display order across ALL fields (required and optional), by id.
   * When present, both the editor and the applicant-facing form render fields
   * in this order — so a required field need not come first. When omitted
   * (legacy data), fields render required-first then optional. Ids missing from
   * the list are appended in their array order; ids in the list but absent from
   * the fields are ignored. See {@link getOrderedFormFields}.
   */
  fieldOrder?: string[];
}

/*
 * Field Value Types
 *
 * These types represent the values collected by each field type in the
 * application form. They correspond to the validation schemas defined in
 * AboutFormInputsDynamic.tsx.
 */

export const NameQuestionId = 'name';

export const DateOfBirthQuestionId = 'dob';

/** Value collected by date fields */
export interface DateOfBirth {
  year: number;
  month: number;
  day: number;
}

/**
 * Minimum age, in years, required to submit any Code in Place application.
 * Below it we cannot collect personal information without verifiable parental
 * consent, which we do not do, so the form refuses the submission. This is a
 * legal floor rather than a course-eligibility rule: it applies to every
 * application type, and there is no older threshold above it.
 */
export const MINIMUM_APPLICANT_AGE = 13;

/**
 * Narrows a partially-filled date of birth - the shape a form validator sees
 * while the applicant is still typing, with any of the three parts still
 * missing - to a complete {@link DateOfBirth}.
 * @param dob - The date-of-birth value to check. `null`/`undefined` (an
 * untouched field) is not complete.
 * @return True when year, month, and day are all whole numbers.
 */
export function isCompleteDateOfBirth(
  dob: Partial<DateOfBirth> | null | undefined,
): dob is DateOfBirth {
  if (isNullOrUndefined(dob)) {
    return false;
  }
  return (
    Number.isInteger(dob.year) &&
    Number.isInteger(dob.month) &&
    Number.isInteger(dob.day)
  );
}

/**
 * Computes age in whole years from a date of birth.
 * @param dob - The date of birth to compute age from.
 * @param now - The reference date to compute age against. Defaults to the
 * current date when omitted.
 * @return The age in whole years as of `now`.
 */
export function getAgeFromDateOfBirth({
  dob,
  now = new Date(),
}: {
  dob: DateOfBirth;
  now?: Date;
}): number {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed.
  const currentDay = now.getDate();

  let age = currentYear - dob.year;
  if (
    currentMonth < dob.month ||
    (currentMonth === dob.month && currentDay < dob.day)
  ) {
    age--;
  }
  return age;
}

export const GeolocationQuestionId = 'location';

/** Value collected by geolocation fields */
export interface Geolocation {
  locality: string;
  country?: string;
  coordinates?: string;
  /**
   * True when the applicant typed `locality` by hand because the place lookup
   * was unreachable, so it was never resolved through Places.
   */
  isManualEntry?: boolean;
}

export const GenderQuestionId = 'gender';

export const GENDER_OPTIONS = ['Female', 'Male', 'Non-binary', 'Other', 'Prefer not to say'] as const;

/** Type representing valid gender values */
export type Gender = (typeof GENDER_OPTIONS)[number];

/** IDs of required static application fields */
export const REQUIRED_STATIC_FIELD_IDS = [
  NameQuestionId,
  GenderQuestionId,
  DateOfBirthQuestionId,
  GeolocationQuestionId,
] as const;

/** Type representing valid required static field IDs */
export type RequiredStaticFieldId = (typeof REQUIRED_STATIC_FIELD_IDS)[number];

/**
 * Values collected from required static application fields.
 * This type represents the shape of data collected from the required
 * fields defined in REQUIRED_STATIC_APPLICATION_FIELDS.
 */
export interface RequiredApplicationFieldValues {
  /** Applicant's full name */
  name: string;
  /** Applicant's gender */
  gender: Gender;
  /** Applicant's date of birth */
  dob: DateOfBirth;
  /** Applicant's location */
  location: Geolocation;
}

/**
 * Type guard to check if a field ID is a required static field
 */
export const isRequiredStaticFieldId = (
  id: string,
): id is RequiredStaticFieldId => {
  return REQUIRED_STATIC_FIELD_IDS.includes(id as RequiredStaticFieldId);
};

export const FIELD_TYPES = [
  {value: 'text', label: 'Text Input'},
  {value: 'longtext', label: 'Long Text Input'},
  {value: 'numeric', label: 'Numeric Input'},
  {value: 'multicheckbox', label: 'Multi Checkbox'},
  {value: 'singlecheckbox', label: 'Single Checkbox'},
  {value: 'dropdown', label: 'Dropdown'},
  {value: 'radio', label: 'Radio Buttons'},
  {value: 'linear_scale', label: 'Linear Scale'},
  {value: 'date', label: 'Date'},
  {value: 'text_display', label: 'Text Display'},
  {value: 'audio_input', label: 'Audio Recording'},
  {value: 'geolocation', label: 'Geolocation Selector'},
];

export const REQUIRED_STATIC_APPLICATION_FIELDS: FormField[] = [
  {
    id: NameQuestionId,
    type: 'text',
    question: 'What is your name?', // default question, can be edited in the form editor
    placeholder: 'Jane Doe', // default placeholder, can be edited in the form editor
    errorMessage: 'Name is required', // default error message, can be edited in the form editor
  },
  {
    id: GenderQuestionId,
    type: 'dropdown',
    question: 'Gender', // default question, can be edited in the form editor
    options: Array.from(GENDER_OPTIONS),
    errorMessage: 'Gender is required', // default error message, can be edited in the form editor
  },
  {
    id: DateOfBirthQuestionId,
    type: 'date',
    question: 'What is your date of birth?', // default question, can be edited in the form editor
  },
  {
    id: GeolocationQuestionId,
    type: 'geolocation',
    question: 'Where are you located?', // default question, can be edited in the form editor
  },
];

export const validateFormInfo = (data: unknown): FormInfo => {
  if (!data || typeof data !== 'object') {
    return {title: '', type: '', fields: [], requiredFields: REQUIRED_STATIC_APPLICATION_FIELDS};
  }

  const formData = data as Record<string, unknown>;
  let title = '';
  let type = '';
  const fields: FormField[] = [];
  const requiredFields: FormField[] = REQUIRED_STATIC_APPLICATION_FIELDS;

  if (typeof formData.title === 'string') {
    title = formData.title;
  }
  if (typeof formData.type === 'string') {
    type = formData.type;
  }

  // Validate and extract fields
  if (Array.isArray(formData.fields)) {
    for (const fieldData of formData.fields) {
      if (!fieldData) {
        console.warn('Invalid field data (null/undefined), skipping');
        continue;
      }

      if (isFormField(fieldData) && !isRequiredStaticFieldId(fieldData.id)) {
        fields.push(fieldData as FormField);
      } else {
        console.warn(
          `Failed to validate field with id ${fieldData?.id || 'unknown'}, skipping`,
        );
      }
    }
  }

  if (Array.isArray(formData.requiredFields)) {
    // we found required fields in the data, so clear the required fields array
    requiredFields.length = 0;
    for (const fieldData of formData.requiredFields) {
      if (!fieldData) {
        console.warn('Invalid required field data (null/undefined), skipping');
        continue;
      }

      if (isFormField(fieldData) && isRequiredStaticFieldId(fieldData.id)) {
        requiredFields.push(fieldData as FormField);
      } else {
        console.warn(
          `Failed to validate required field with id ${fieldData?.id || 'unknown'}, skipping`,
        );
      }
    }
  }

  let fieldOrder: string[] | undefined;
  if (Array.isArray(formData.fieldOrder)) {
    const ids = formData.fieldOrder.filter(
      (id): id is string => typeof id === 'string',
    );
    if (ids.length > 0) {
      fieldOrder = ids;
    }
  }

  return {title, type, fields, requiredFields, ...(fieldOrder && {fieldOrder})};
};

/**
 * Combines a form's required and optional fields into a single list in display
 * order.
 *
 * Ordering rules:
 * - When `fieldOrder` is present and non-empty, fields are returned in that
 *   order. Ids listed but not found among the fields are skipped; fields whose
 *   id is not listed are appended afterwards in their array order (required
 *   before optional). Duplicate ids in `fieldOrder` are placed once.
 * - When `fieldOrder` is absent or empty (legacy data), fields are returned
 *   required-first, then optional — the historical behavior.
 *
 * @param formInfo - The form data, or a subset of it. When omitted or missing
 *   `requiredFields`, the required static fields are used; missing `fields`
 *   defaults to an empty list.
 * @returns The ordered, combined list of fields.
 */
export const getOrderedFormFields = (formInfo?: {
  requiredFields?: FormField[];
  fields?: FormField[];
  fieldOrder?: string[];
}): FormField[] => {
  const requiredFields =
    formInfo?.requiredFields ?? REQUIRED_STATIC_APPLICATION_FIELDS;
  const fields = formInfo?.fields ?? [];
  const combined = [...requiredFields, ...fields];

  const order = formInfo?.fieldOrder;
  if (!order || order.length === 0) {
    return combined;
  }

  const byId = new Map(combined.map(field => [field.id, field]));
  const ordered: FormField[] = [];
  const placed = new Set<string>();
  for (const id of order) {
    const field = byId.get(id);
    if (field && !placed.has(id)) {
      ordered.push(field);
      placed.add(id);
    }
  }
  // Append fields not covered by `fieldOrder` (newly added or stale order).
  for (const field of combined) {
    if (!placed.has(field.id)) {
      ordered.push(field);
    }
  }
  return ordered;
};

// Validation functions for field data types
const isTextInputField = (object: unknown): object is TextInputField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as TextInputField;

  return (
    typeof field.id === 'string' &&
    field.type === 'text' &&
    typeof field.question === 'string' &&
    typeof field.placeholder === 'string' &&
    typeof field.errorMessage === 'string'
  );
};

const isLongTextInputField = (
  object: unknown,
): object is LongTextInputField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as LongTextInputField;

  return (
    typeof field.id === 'string' &&
    field.type === 'longtext' &&
    typeof field.question === 'string' &&
    typeof field.description === 'string' &&
    typeof field.placeholder === 'string' &&
    typeof field.errorMessage === 'string'
  );
};

const isNumericInputField = (object: unknown): object is NumericInputField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as NumericInputField;

  return (
    typeof field.id === 'string' &&
    field.type === 'numeric' &&
    typeof field.question === 'string' &&
    typeof field.placeholder === 'string' &&
    typeof field.errorMessage === 'string'
  );
};

const isMultiCheckboxField = (
  object: unknown,
): object is MultiCheckboxField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as MultiCheckboxField;

  return (
    typeof field.id === 'string' &&
    field.type === 'multicheckbox' &&
    typeof field.question === 'string' &&
    typeof field.description === 'string' &&
    Array.isArray(field.options) &&
    field.options.every(option => typeof option === 'string') &&
    (field.requireAll === undefined || typeof field.requireAll === 'boolean') &&
    (field.errorMessage === undefined || typeof field.errorMessage === 'string') &&
    (field.hasOther === undefined || typeof field.hasOther === 'boolean')
  );
};

const isSingleCheckboxField = (
  object: unknown,
): object is SingleCheckboxField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as SingleCheckboxField;

  return (
    typeof field.id === 'string' &&
    field.type === 'singlecheckbox' &&
    typeof field.question === 'string' &&
    typeof field.description === 'string' &&
    typeof field.checkboxLabel === 'string' &&
    typeof field.errorMessage === 'string'
  );
};

const isDropdownField = (object: unknown): object is DropdownField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as DropdownField;

  return (
    typeof field.id === 'string' &&
    field.type === 'dropdown' &&
    typeof field.question === 'string' &&
    Array.isArray(field.options) &&
    field.options.every(option => typeof option === 'string') &&
    typeof field.errorMessage === 'string'
  );
};

const isRadioField = (object: unknown): object is RadioField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as RadioField;

  return (
    typeof field.id === 'string' &&
    field.type === 'radio' &&
    typeof field.question === 'string' &&
    Array.isArray(field.options) &&
    field.options.every(option => typeof option === 'string') &&
    typeof field.errorMessage === 'string'
  );
};

const isLinearScaleField = (object: unknown): object is LinearScaleField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as LinearScaleField;

  return (
    typeof field.id === 'string' &&
    field.type === 'linear_scale' &&
    typeof field.question === 'string' &&
    typeof field.min === 'number' &&
    typeof field.max === 'number' &&
    (field.minLabel === undefined || typeof field.minLabel === 'string') &&
    (field.maxLabel === undefined || typeof field.maxLabel === 'string') &&
    (field.errorMessage === undefined || typeof field.errorMessage === 'string')
  );
};

const isDateField = (object: unknown): object is DateField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as DateField;

  return (
    typeof field.id === 'string' &&
    field.type === 'date' &&
    typeof field.question === 'string'
  );
};

const isTextField = (object: unknown): object is TextField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as TextField;

  return (
    typeof field.id === 'string' &&
    field.type === 'text_display' &&
    typeof field.question === 'string' &&
    typeof field.subtext === 'string'
  );
};

const isAudioField = (object: unknown): object is AudioField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as AudioField;

  return (
    typeof field.id === 'string' &&
    field.type === 'audio_input' &&
    typeof field.question === 'string' &&
    typeof field.description === 'string'
  );
};

const isGeolocationField = (object: unknown): object is GeolocationField => {
  if (!object || typeof object !== 'object') return false;
  const field = object as GeolocationField;

  return (
    typeof field.id === 'string' &&
    field.type === 'geolocation' &&
    typeof field.question === 'string'
  );
};

const isFormField = (object: unknown): object is FormField => {
  if (!object || typeof object !== 'object') {
    return false;
  }

  const field = object as FormField;
  if (typeof field.type !== 'string') {
    return false;
  }

  switch (field.type) {
    case 'text':
      return isTextInputField(object);
    case 'longtext':
      return isLongTextInputField(object);
    case 'numeric':
      return isNumericInputField(object);
    case 'multicheckbox':
      return isMultiCheckboxField(object);
    case 'singlecheckbox':
      return isSingleCheckboxField(object);
    case 'dropdown':
      return isDropdownField(object);
    case 'radio':
      return isRadioField(object);
    case 'linear_scale':
      return isLinearScaleField(object);
    case 'date':
      return isDateField(object);
    case 'text_display':
      return isTextField(object);
    case 'audio_input':
      return isAudioField(object);
    case 'geolocation':
      return isGeolocationField(object);
    default:
      return false;
  }
};

/*
 * Below are utility and field validation functions for
 * the field editor inputs.
 */
export const createNewField = (
  type: ApplicationFieldType,
  fieldId: string,
): FormField => {
  const baseField = {id: fieldId, question: ''};

  return {
    text: (): TextInputField => ({
      ...baseField,
      type: 'text',
      placeholder: '',
      errorMessage: '',
    }),
    longtext: (): LongTextInputField => ({
      ...baseField,
      type: 'longtext',
      description: '',
      placeholder: '',
      errorMessage: '',
    }),
    numeric: (): NumericInputField => ({
      ...baseField,
      type: 'numeric',
      placeholder: '',
      errorMessage: '',
    }),
    multicheckbox: (): MultiCheckboxField => ({
      ...baseField,
      type: 'multicheckbox',
      description: '',
      options: [''],
      requireAll: false,
      hasOther: false,
    }),
    singlecheckbox: (): SingleCheckboxField => ({
      ...baseField,
      type: 'singlecheckbox',
      description: '',
      checkboxLabel: '',
      errorMessage: '',
    }),
    dropdown: (): DropdownField => ({
      ...baseField,
      type: 'dropdown',
      options: [''],
      errorMessage: '',
    }),
    radio: (): RadioField => ({
      ...baseField,
      type: 'radio',
      options: [''],
      errorMessage: '',
    }),
    linear_scale: (): LinearScaleField => ({
      ...baseField,
      type: 'linear_scale',
      min: 1,
      max: 5,
      minLabel: '',
      maxLabel: '',
      errorMessage: '',
    }),
    date: (): DateField => ({
      ...baseField,
      type: 'date',
    }),
    text_display: (): TextField => ({
      ...baseField,
      type: 'text_display',
      subtext: '',
    }),
    audio_input: (): AudioField => ({
      ...baseField,
      type: 'audio_input',
      description: '',
    }),
    geolocation: (): GeolocationField => ({
      ...baseField,
      type: 'geolocation',
    }),
  }[type]();
};

// Field validation functions for inputs
export const validateField = (field: FormField): boolean => {
  if (!field.question.trim()) return false;

  switch (field.type as ApplicationFieldType) {
    case 'text':
      return validateTextField(field as TextInputField);
    case 'longtext':
      return validateLongTextField(field as LongTextInputField);
    case 'numeric':
      return validateNumericField(field as NumericInputField);
    case 'multicheckbox':
      return validateMultiCheckboxField(field as MultiCheckboxField);
    case 'singlecheckbox':
      return validateSingleCheckboxField(field as SingleCheckboxField);
    case 'dropdown':
      return validateDropdownField(field as DropdownField);
    case 'radio':
      return validateRadioField(field as RadioField);
    case 'linear_scale':
      return validateLinearScaleField(field as LinearScaleField);
    case 'date':
    case 'text_display':
    case 'audio_input':
    case 'geolocation':
      return true;
    default:
      return true;
  }
};

// Placeholder is optional (an example hint); only the error message is
// required for text-like fields.
const validateTextField = (field: TextInputField): boolean => {
  return !!field.errorMessage?.trim();
};

const validateLongTextField = (field: LongTextInputField): boolean => {
  return !!field.errorMessage?.trim();
};

const validateNumericField = (field: NumericInputField): boolean => {
  return !!field.errorMessage?.trim();
};

const validateMultiCheckboxField = (field: MultiCheckboxField): boolean => {
  return field.options.some((option: string) => option?.trim());
};

const validateSingleCheckboxField = (field: SingleCheckboxField): boolean => {
  return !!field.checkboxLabel.trim() && !!field.errorMessage?.trim();
};

const validateDropdownField = (field: DropdownField): boolean => {
  return (
    field.options.some((option: string) => option.trim()) &&
    !!field.errorMessage?.trim()
  );
};

const validateRadioField = (field: RadioField): boolean => {
  return (
    field.options.some((option: string) => option.trim()) &&
    !!field.errorMessage?.trim()
  );
};

const validateLinearScaleField = (field: LinearScaleField): boolean => {
  return (
    Number.isInteger(field.min) &&
    Number.isInteger(field.max) &&
    field.max > field.min
  );
};
